import type { AnimationEvent } from "@angular/animations";
import { ApplicationInsightsService } from "@ankaadia/ankaadia-shared-frontend";
import { isNil } from "lodash";
import { MultiSelect } from "primeng/multiselect";
import { Select } from "primeng/select";
import { TreeSelect } from "primeng/treeselect";

type FilterableSelect = Select | MultiSelect | TreeSelect;

const OVERLAY_PANEL_SELECTOR = ".p-select-overlay, .p-multiselect-overlay, .p-treeselect-overlay";

const widthKeepers = new WeakMap<HTMLElement, ResizeObserver>();

/**
 * Runs `action` and swallows any exception, tracking it instead. Every entry point below runs
 * inside someone else's call stack — PrimeNG's own animation handling, or a body-wide DOM observer
 * that lives for the whole app session — so a bug in this file must not surface as an uncaught
 * exception there. Worst case on failure: this one overlay stops staying pinned.
 */
function guarded(context: string, action: () => void): void {
  try {
    action();
  } catch (error) {
    const trackedError = error instanceof Error ? error : new Error(String(error));
    ApplicationInsightsService.trackException(trackedError, { context: `Select overlay width fix: ${context}` });
  }
}

/**
 * PrimeNG sizes a select overlay to the widest option that is currently rendered, so filtering
 * resizes it on every keystroke. Re-applying the widest width the panel has had keeps it stable
 * while the user types, without squeezing every overlay down to the width of its select.
 */
function keepPanelWidth(panel: HTMLElement): void {
  if (widthKeepers.has(panel)) {
    return;
  }

  // A ResizeObserver rather than a single measurement when the overlay opens: it also picks up
  // options that arrive after opening, and its callbacks run after layout but before paint, so the
  // shrunk width is never actually painted. It is not patched by zone.js in this app (the
  // `zone-patch-resize-observer` plugin is never imported), so these callbacks run natively and
  // never trigger Angular change detection.
  let widestWidth = 0;
  const observer = new ResizeObserver(() =>
    guarded("ResizeObserver callback", () => {
      const width = Math.ceil(panel.getBoundingClientRect().width);
      if (width > widestWidth) {
        widestWidth = width;
        return;
      }

      // Writing the same value again would be a no-op anyway, but skipping it rules out any chance
      // of a resize loop.
      const widestWidthInPixels = `${widestWidth}px`;
      if (panel.style.minWidth !== widestWidthInPixels) {
        panel.style.minWidth = widestWidthInPixels;
      }
    })
  );

  observer.observe(panel);
  widthKeepers.set(panel, observer);
}

function releasePanelWidth(panel: HTMLElement): void {
  widthKeepers.get(panel)?.disconnect();
  widthKeepers.delete(panel);
}

function findPanel(node: Node | null | undefined): HTMLElement | null {
  if (!(node instanceof HTMLElement)) {
    return null;
  }
  return node.matches(OVERLAY_PANEL_SELECTOR) ? node : node.querySelector(OVERLAY_PANEL_SELECTOR);
}

// Overlays are appended to the body (see the global `overlayOptions` in main.ts), so watching the
// body picks them up without depending on PrimeNG internals. Registered before the application
// bootstraps, while Zone.current is still the root zone: zone.js captures the zone a MutationObserver
// was registered in, not the zone active when a mutation happens, so this callback runs in the root
// zone for the app's whole lifetime and never triggers Angular change detection.
const overlayWatcher = new MutationObserver((mutations) =>
  guarded("body MutationObserver callback", () => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        const panel = findPanel(node);
        if (!isNil(panel)) {
          keepPanelWidth(panel);
        }
      });
      mutation.removedNodes.forEach((node) => {
        const panel = findPanel(node);
        if (!isNil(panel)) {
          releasePanelWidth(panel);
        }
      });
    });
  })
);

overlayWatcher.observe(document.body, { childList: true });

/**
 * Second way in, for overlays that never pass through the body — an `[appendTo]` other than the
 * configured default would keep them inside their component. Both paths end up in the same
 * (idempotent) `keepPanelWidth`, so it does not matter which one wins.
 */
function watchOverlayOf<T extends FilterableSelect>(component: { prototype: T }): void {
  const onOverlayAnimationStart = component.prototype.onOverlayAnimationStart;
  if (typeof onOverlayAnimationStart !== "function") {
    // eslint-disable-next-line no-console
    console.warn("Skipping part of the select overlay width fix — it does not match this PrimeNG version.");
    return;
  }

  component.prototype.onOverlayAnimationStart = function (this: T, event: AnimationEvent): void {
    // Always run PrimeNG's own handling first and un-guarded: a bug in our code must never prevent
    // or roll back its focus/scroll/filter behavior.
    onOverlayAnimationStart.call(this, event);

    guarded("onOverlayAnimationStart hook", () => {
      const panel = findPanel(this.overlayViewChild?.overlayViewChild?.nativeElement);
      if (isNil(panel)) {
        return;
      }

      if (event.toState === "visible") {
        keepPanelWidth(panel);
      } else {
        releasePanelWidth(panel);
      }
    });
  };
}

// Intentionally not done via a `pnpm patch` against the primeng package itself (see
// patches/primeng.patch, which already patches two other PrimeNG bugs and hard-fails `pnpm install`
// if a version bump invalidates it). A vendor patch would give that same tripwire here, but at the
// cost of duplicating this logic across three separate compiled bundles (primeng-select/-multiselect/
// -treeselect.mjs) with no lint or type checking. This stays app code and degrades safely instead
// (see `guarded` above) if PrimeNG ever changes the internals it relies on.
watchOverlayOf(Select);
watchOverlayOf(MultiSelect);
watchOverlayOf(TreeSelect);
